import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ScanLine, Camera, Keyboard, CheckCircle2, AlertTriangle,
  Package, ArrowLeft, ChevronRight, X,
} from 'lucide-react';
import { cn } from '../lib/utils.js';
import { useScanItem, useSellItem, type ScannedItem } from '../hooks/useParts.js';

type View = 'scanner' | 'confirm' | 'success';

export default function ScanItemPage() {
  const { id: jobId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [view, setView] = useState<View>('scanner');
  const [qrInput, setQrInput] = useState('');
  const [useCamera, setUseCamera] = useState(false);
  const [scannedData, setScannedData] = useState<ScannedItem | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [billable, setBillable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [soldItem, setSoldItem] = useState<{
    name: string;
    qty: number;
    price: number;
    billable: boolean;
  } | null>(null);

  const scanMutation = useScanItem();
  const sellMutation = useSellItem(jobId);

  // ─── Camera scanning ──────────────────────────────────────

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      // Use BarcodeDetector if available (Chrome/Edge), otherwise fall back
      if ('BarcodeDetector' in window) {
        const detector = new (window as unknown as { BarcodeDetector: new (opts: { formats: string[] }) => {
          detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>>;
        } }).BarcodeDetector({ formats: ['qr_code'] });

        scanIntervalRef.current = setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0 && barcodes[0].rawValue) {
              handleScan(barcodes[0].rawValue);
            }
          } catch {
            // detection frame failed, ignore and retry
          }
        }, 300);
      } else {
        // BarcodeDetector not available — show message
        setError('Camera QR scanning requires Chrome or Edge. Use the text input instead.');
        stopCamera();
        setUseCamera(false);
      }
    } catch {
      setError('Camera permission denied. Use the text input to enter the QR code manually.');
      setUseCamera(false);
    }
  }, [stopCamera]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (useCamera && view === 'scanner') {
      startCamera();
    } else {
      stopCamera();
    }
    return stopCamera;
  }, [useCamera, view, startCamera, stopCamera]);

  // ─── Handle scan / lookup ─────────────────────────────────

  async function handleScan(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;

    // Stop camera scanning while we look up
    stopCamera();
    setError(null);

    try {
      const data = await scanMutation.mutateAsync(trimmed);
      setScannedData(data);
      setQuantity(1);
      setBillable(true);
      setView('confirm');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (message.includes('not found') || message.includes('404')) {
        setError('Item not found in inventory. Try a different code or add the part manually.');
      } else {
        setError(`Scan failed: ${message}`);
      }
    }
  }

  function handleInputSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleScan(qrInput);
  }

  // ─── Handle sell ──────────────────────────────────────────

  async function handleConfirmSell() {
    if (!jobId || !scannedData) return;

    const item = scannedData.item;
    const unitPrice = item.sell_price ?? item.cost_price ?? 0;

    try {
      await sellMutation.mutateAsync({
        job_id: jobId,
        product_id: item.id,
        product_name: item.name,
        sku: item.sku ?? item.barcode,
        serial_number: item.serial_number,
        quantity,
        unit_price: unitPrice,
        billable,
      });

      setSoldItem({
        name: item.name,
        qty: quantity,
        price: unitPrice * quantity,
        billable,
      });
      setView('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add item to job');
    }
  }

  // ─── Reset to scanner ────────────────────────────────────

  function resetToScanner() {
    setView('scanner');
    setScannedData(null);
    setSoldItem(null);
    setQrInput('');
    setError(null);
    setQuantity(1);
    setBillable(true);
  }

  // ─── Scanner View ─────────────────────────────────────────

  if (view === 'scanner') {
    return (
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border bg-card p-4">
          <button
            onClick={() => navigate(`/jobs/${jobId}`)}
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-foreground">Scan & Sell</h1>
            <p className="text-xs text-muted-foreground">Scan a QR code on the item label</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* Camera / Text toggle */}
          <div className="mb-4 flex rounded-xl border border-border bg-card p-1">
            <button
              onClick={() => setUseCamera(false)}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium transition-colors',
                !useCamera ? 'bg-primary text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Keyboard className="h-4 w-4" />
              Text / Scanner
            </button>
            <button
              onClick={() => setUseCamera(true)}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium transition-colors',
                useCamera ? 'bg-primary text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Camera className="h-4 w-4" />
              Camera
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-400">{error}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => setError(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => navigate(`/jobs/${jobId}/parts`)}
                    className="text-xs text-primary hover:underline"
                  >
                    Add part manually
                  </button>
                </div>
              </div>
            </div>
          )}

          {useCamera ? (
            /* Camera viewfinder */
            <div className="relative overflow-hidden rounded-xl border border-border bg-black">
              <video
                ref={videoRef}
                className="aspect-square w-full object-cover"
                playsInline
                muted
              />
              {/* Scan overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-48 w-48 rounded-2xl border-2 border-primary/60" />
              </div>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-sm font-medium text-white drop-shadow-lg">
                  Point camera at QR code
                </p>
              </div>
              {scanMutation.isPending && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              )}
            </div>
          ) : (
            /* Text input for QR code / barcode scanner */
            <form onSubmit={handleInputSubmit} className="space-y-4">
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="mb-4 flex flex-col items-center text-center">
                  <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                    <ScanLine className="h-8 w-8 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Scan with a Bluetooth barcode scanner or type the QR code value
                  </p>
                </div>
                <input
                  value={qrInput}
                  onChange={e => setQrInput(e.target.value)}
                  placeholder="theone:inv:asset:..."
                  autoFocus
                  autoComplete="off"
                  className="w-full rounded-xl border border-border bg-secondary px-4 py-4 text-center text-lg text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={!qrInput.trim() || scanMutation.isPending}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-base font-semibold text-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {scanMutation.isPending ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <ScanLine className="h-5 w-5" />
                    Look Up Item
                  </>
                )}
              </button>
            </form>
          )}

          {/* Quick link to manual add */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(`/jobs/${jobId}/parts`)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Or add a part manually
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Confirm View ─────────────────────────────────────────

  if (view === 'confirm' && scannedData) {
    const item = scannedData.item;
    const unitPrice = item.sell_price ?? item.cost_price ?? 0;
    const totalPrice = unitPrice * quantity;

    return (
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border bg-card p-4">
          <button
            onClick={resetToScanner}
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-bold text-foreground">Confirm Item</h1>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-400 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto shrink-0">
                <X className="h-4 w-4 text-red-400" />
              </button>
            </div>
          )}

          {/* Product card */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-1 flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold text-foreground leading-tight">{item.name}</h2>
                {item.sku && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    SKU: {item.sku}
                  </p>
                )}
                {item.barcode && item.barcode !== item.sku && (
                  <p className="text-sm text-muted-foreground">
                    Barcode: {item.barcode}
                  </p>
                )}
              </div>
            </div>

            {item.description && (
              <p className="mt-3 text-sm text-muted-foreground">{item.description}</p>
            )}

            {item.serial_number && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-secondary px-3 py-2">
                <span className="text-xs font-medium text-muted-foreground">S/N:</span>
                <span className="text-sm font-mono text-foreground">{item.serial_number}</span>
              </div>
            )}

            {item.category && (
              <span className="mt-3 inline-block rounded-full bg-secondary px-2.5 py-1 text-xs text-muted-foreground">
                {item.category}
              </span>
            )}
          </div>

          {/* Price */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Unit Price</span>
              <span className="text-2xl font-bold text-green-400">
                ${unitPrice.toFixed(2)}
              </span>
            </div>
            {quantity > 1 && (
              <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                <span className="text-sm text-muted-foreground">Total ({quantity} items)</span>
                <span className="text-lg font-semibold text-green-400">
                  ${totalPrice.toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Quantity */}
          <div className="rounded-xl border border-border bg-card p-5">
            <label className="mb-2 block text-sm font-medium text-foreground">Quantity</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-secondary text-lg font-bold text-foreground hover:bg-secondary/80"
              >
                -
              </button>
              <input
                type="number"
                value={quantity}
                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                min="1"
                className="h-12 flex-1 rounded-xl border border-border bg-secondary px-4 text-center text-lg font-bold text-foreground focus:border-primary focus:outline-none"
              />
              <button
                onClick={() => setQuantity(q => q + 1)}
                className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-secondary text-lg font-bold text-foreground hover:bg-secondary/80"
              >
                +
              </button>
            </div>
          </div>

          {/* Billable toggle */}
          <div className="rounded-xl border border-border bg-card p-5">
            <label className="mb-3 block text-sm font-medium text-foreground">Billing</label>
            <div className="flex gap-3">
              <button
                onClick={() => setBillable(true)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 rounded-xl py-4 text-base font-semibold transition-colors',
                  billable
                    ? 'bg-green-500/20 text-green-400 border-2 border-green-500/50'
                    : 'bg-secondary text-muted-foreground border-2 border-transparent hover:border-border'
                )}
              >
                Billable
              </button>
              <button
                onClick={() => setBillable(false)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 rounded-xl py-4 text-base font-semibold transition-colors',
                  !billable
                    ? 'bg-secondary text-foreground border-2 border-border'
                    : 'bg-secondary text-muted-foreground border-2 border-transparent hover:border-border'
                )}
              >
                Non-Billable
              </button>
            </div>
          </div>

          {/* Actions */}
          <button
            onClick={handleConfirmSell}
            disabled={sellMutation.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-base font-semibold text-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {sellMutation.isPending ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5" />
                Confirm & Add to Job
              </>
            )}
          </button>

          <div className="flex gap-3 text-center">
            <button
              onClick={resetToScanner}
              className="flex-1 rounded-xl border border-border py-3 text-sm text-muted-foreground hover:bg-secondary"
            >
              Scan Different Item
            </button>
            <button
              onClick={() => navigate(`/jobs/${jobId}/parts`)}
              className="text-sm text-muted-foreground hover:text-foreground py-3"
            >
              Not the right item?
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Success View ─────────────────────────────────────────

  if (view === 'success' && soldItem) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6 text-center">
          {/* Success icon */}
          <div className="flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/20">
              <CheckCircle2 className="h-10 w-10 text-green-400" />
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">Item Added to Job!</h2>
            <p className="mt-1 text-muted-foreground">The part has been recorded</p>
          </div>

          {/* Summary card */}
          <div className="rounded-xl border border-border bg-card p-5 text-left space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Item</span>
              <span className="text-sm font-medium text-foreground">{soldItem.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Quantity</span>
              <span className="text-sm font-medium text-foreground">{soldItem.qty}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="text-sm font-bold text-green-400">${soldItem.price.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Billing</span>
              <span className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                soldItem.billable
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-secondary text-muted-foreground'
              )}>
                {soldItem.billable ? 'Billable' : 'Non-Billable'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={resetToScanner}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-base font-semibold text-foreground hover:bg-primary/90"
            >
              <ScanLine className="h-5 w-5" />
              Scan Another Item
            </button>
            <button
              onClick={() => navigate(`/jobs/${jobId}`)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm text-muted-foreground hover:bg-secondary"
            >
              Back to Job
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}
